if (!customElements.get('cart-popular-add-ons')) {
  customElements.define(
    'cart-popular-add-ons',
    class CartPopularAddOns extends HTMLElement {
      connectedCallback() {
        this.productList = this.querySelector('.cart-popular-add-ons__list');
        this.scrollIndicator = this.querySelector('.cart-popular-add-ons__scroll-indicator');
        this.scrollThumb = this.querySelector('.cart-popular-add-ons__scroll-thumb');
        this.scrollFrame = null;
        this.scrollController = null;
        this.initialiseScrollIndicator();
      }

      disconnectedCallback() {
        this.scrollController?.abort();
        this.scrollObserver?.disconnect();
        this.scrollIndicator?.removeAttribute('data-scroll-ready');
        this.productList?.classList.remove('is-scrollbar-dragging');
        this.scrollIndicator?.classList.remove('is-dragging');
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      }

      initialiseScrollIndicator() {
        if (!this.productList || !this.scrollIndicator || !this.scrollThumb) return;
        if (this.scrollIndicator.dataset.scrollReady === 'true') return;

        this.scrollIndicator.dataset.scrollReady = 'true';
        this.scrollController = new AbortController();
        const { signal } = this.scrollController;
        const scheduleUpdate = () => {
          if (this.scrollFrame) return;

          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            this.updateScrollIndicator();
          });
        };
        const onPointerDown = (event) => {
          const indicatorRect = this.scrollIndicator.getBoundingClientRect();
          const thumbRect = this.scrollThumb.getBoundingClientRect();
          const maximumScroll = Math.max(0, this.productList.scrollWidth - this.productList.clientWidth);
          const maximumTravel = Math.max(0, indicatorRect.width - thumbRect.width);

          if (maximumScroll <= 0 || maximumTravel <= 0) return;

          event.preventDefault();

          if (event.clientX >= thumbRect.left && event.clientX <= thumbRect.right) {
            this.scrollDragging = true;
            this.scrollDragStartX = event.clientX;
            this.scrollDragStartLeft = this.productList.scrollLeft;
            this.scrollDragMaximumScroll = maximumScroll;
            this.scrollDragMaximumTravel = maximumTravel;
            this.productList.classList.add('is-scrollbar-dragging');
            this.scrollIndicator.classList.add('is-dragging');
            this.scrollIndicator.setPointerCapture?.(event.pointerId);
            return;
          }

          const clickPosition = event.clientX - indicatorRect.left - thumbRect.width / 2;
          const progress = Math.min(1, Math.max(0, clickPosition / maximumTravel));
          this.productList.scrollTo({
            left: progress * maximumScroll,
            behavior: 'smooth',
          });
        };
        const onPointerMove = (event) => {
          if (!this.scrollDragging) return;

          event.preventDefault();
          const delta = event.clientX - this.scrollDragStartX;
          const scrollLeft =
            this.scrollDragStartLeft +
            delta * (this.scrollDragMaximumScroll / this.scrollDragMaximumTravel);
          this.productList.scrollLeft = Math.min(
            this.scrollDragMaximumScroll,
            Math.max(0, scrollLeft)
          );
        };
        const endPointerDrag = (event) => {
          if (!this.scrollDragging) return;

          this.scrollDragging = false;
          this.productList.classList.remove('is-scrollbar-dragging');
          this.scrollIndicator.classList.remove('is-dragging');
          if (this.scrollIndicator.hasPointerCapture?.(event.pointerId)) {
            this.scrollIndicator.releasePointerCapture(event.pointerId);
          }
        };
        const onKeyDown = (event) => {
          const maximumScroll = Math.max(0, this.productList.scrollWidth - this.productList.clientWidth);
          if (maximumScroll <= 0) return;

          let nextScrollLeft = null;
          if (event.key === 'ArrowLeft') {
            nextScrollLeft = this.productList.scrollLeft - this.productList.clientWidth * 0.8;
          }
          if (event.key === 'ArrowRight') {
            nextScrollLeft = this.productList.scrollLeft + this.productList.clientWidth * 0.8;
          }
          if (event.key === 'Home') nextScrollLeft = 0;
          if (event.key === 'End') nextScrollLeft = maximumScroll;

          if (nextScrollLeft === null) return;

          event.preventDefault();
          this.productList.scrollTo({ left: nextScrollLeft, behavior: 'smooth' });
        };

        this.scrollDragging = false;
        this.scrollDragStartX = 0;
        this.scrollDragStartLeft = 0;
        this.scrollDragMaximumScroll = 0;
        this.scrollDragMaximumTravel = 0;

        this.productList.addEventListener('scroll', scheduleUpdate, { passive: true, signal });
        window.addEventListener('resize', scheduleUpdate, { passive: true, signal });
        this.scrollIndicator.addEventListener('pointerdown', onPointerDown, { signal });
        window.addEventListener('pointermove', onPointerMove, { passive: false, signal });
        window.addEventListener('pointerup', endPointerDrag, { signal });
        window.addEventListener('pointercancel', endPointerDrag, { signal });
        this.scrollIndicator.addEventListener('keydown', onKeyDown, { signal });

        if ('ResizeObserver' in window) {
          this.scrollObserver = new ResizeObserver(scheduleUpdate);
          this.scrollObserver.observe(this.productList);
          this.scrollObserver.observe(this.scrollIndicator);
        }

        scheduleUpdate();
      }

      updateScrollIndicator() {
        const scrollWidth = this.productList.scrollWidth;
        const visibleWidth = this.productList.clientWidth;
        const trackWidth = this.scrollIndicator.clientWidth;
        const maximumScroll = Math.max(0, scrollWidth - visibleWidth);
        const visibleRatio = scrollWidth > 0 ? Math.min(1, visibleWidth / scrollWidth) : 1;
        const thumbWidth = Math.min(trackWidth, Math.max(24, trackWidth * visibleRatio));
        const maximumTravel = Math.max(0, trackWidth - thumbWidth);
        const progress = maximumScroll > 0
          ? Math.min(1, Math.max(0, this.productList.scrollLeft / maximumScroll))
          : 0;

        this.scrollThumb.style.width = `${thumbWidth}px`;
        this.scrollThumb.style.transform = `translateX(${maximumTravel * progress}px)`;
        this.scrollIndicator.classList.toggle('is-hidden', maximumScroll <= 1);
        this.scrollIndicator.hidden = maximumScroll <= 1;
        this.scrollIndicator.setAttribute('aria-hidden', String(maximumScroll <= 1));
        this.scrollIndicator.setAttribute('tabindex', maximumScroll > 1 ? '0' : '-1');
        this.scrollIndicator.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
        this.scrollIndicator.setAttribute('aria-valuetext', `${Math.round(progress * 100)}% scrolled`);
      }
    }
  );
}

if (!customElements.get('cart-popular-add-on')) {
  customElements.define(
    'cart-popular-add-on',
    class CartPopularAddOn extends HTMLElement {
      constructor() {
        super();
        this.onSubmit = this.onSubmit.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onVariantOptionClick = this.onVariantOptionClick.bind(this);
        this.onVariantToggle = this.onVariantToggle.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.positionVariantOptions = this.positionVariantOptions.bind(this);
        this.variantOptionsPlaceholder = null;
      }

      connectedCallback() {
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        this.variantSelect = this.querySelector('[data-variant-select]');
        this.variantPicker = this.querySelector('[data-variant-picker]');
        this.variantDetails = this.querySelector('[data-variant-details]');
        this.variantToggle = this.querySelector('[data-variant-toggle]');
        this.variantSelected = this.querySelector('[data-variant-selected]');
        this.variantOptions = this.querySelectorAll('[data-variant-option]');
        this.variantOptionsContainer = this.querySelector('[data-variant-options]');
        this.variantLinks = this.querySelectorAll('[data-variant-link]');
        this.image = this.querySelector('.cart-popular-add-on__image');
        this.priceElement = this.querySelector('[data-price]');
        this.compareAtPriceElement = this.querySelector('[data-compare-price]');
        this.errorElement = this.querySelector('.cart-popular-add-on__error');

        if (!this.form || !this.submitButton) return;

        this.form.addEventListener('submit', this.onSubmit);
        this.variantSelect?.addEventListener('change', this.onVariantChange);
        this.variantOptions.forEach((option) => option.addEventListener('click', this.onVariantOptionClick));
        this.variantToggle?.addEventListener('click', this.onVariantToggle);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
        window.addEventListener('resize', this.positionVariantOptions);
        window.addEventListener('scroll', this.positionVariantOptions, true);
        this.variantPicker?.classList.add('is-enhanced');
        this.onVariantChange();
      }

      disconnectedCallback() {
        this.setVariantOptionsOpen(false);
        this.form?.removeEventListener('submit', this.onSubmit);
        this.variantSelect?.removeEventListener('change', this.onVariantChange);
        this.variantOptions?.forEach((option) => option.removeEventListener('click', this.onVariantOptionClick));
        this.variantToggle?.removeEventListener('click', this.onVariantToggle);
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('resize', this.positionVariantOptions);
        window.removeEventListener('scroll', this.positionVariantOptions, true);
      }

      onVariantToggle(event) {
        event.preventDefault();
        this.setVariantOptionsOpen(this.variantOptionsContainer?.hidden === true);
      }

      onDocumentClick(event) {
        if (!this.contains(event.target) && !this.variantOptionsContainer?.contains(event.target)) {
          this.setVariantOptionsOpen(false);
        }
      }

      onKeydown(event) {
        if (event.key === 'Escape' && this.variantOptionsContainer?.hidden === false) {
          event.preventDefault();
          this.setVariantOptionsOpen(false);
          this.variantToggle?.focus();
        }
      }

      setVariantOptionsOpen(isOpen) {
        if (!this.variantOptionsContainer || !this.variantToggle) return;

        if (isOpen && this.variantOptionsContainer.parentNode !== document.body) {
          this.variantOptionsPlaceholder = document.createComment('cart-popular-add-on-variant-options');
          this.variantOptionsContainer.parentNode.insertBefore(
            this.variantOptionsPlaceholder,
            this.variantOptionsContainer
          );
          document.body.appendChild(this.variantOptionsContainer);
        }

        this.variantOptionsContainer.hidden = !isOpen;
        this.variantToggle.setAttribute('aria-expanded', String(isOpen));
        this.variantDetails?.classList.toggle('is-open', isOpen);

        if (isOpen) {
          this.variantOptionsContainer.style.setProperty('display', 'grid', 'important');
          this.variantOptionsContainer.style.setProperty('visibility', 'visible', 'important');
          this.variantOptionsContainer.style.setProperty('opacity', '1', 'important');
          this.variantOptionsContainer.style.setProperty('pointer-events', 'auto', 'important');
          requestAnimationFrame(this.positionVariantOptions);
          return;
        }

        this.variantOptionsContainer.style.removeProperty('display');
        this.variantOptionsContainer.style.removeProperty('visibility');
        this.variantOptionsContainer.style.removeProperty('opacity');
        this.variantOptionsContainer.style.removeProperty('pointer-events');
        this.variantOptionsContainer.style.removeProperty('top');
        this.variantOptionsContainer.style.removeProperty('left');
        this.variantOptionsContainer.style.removeProperty('width');
        this.variantOptionsContainer.style.removeProperty('max-height');

        if (this.variantOptionsPlaceholder?.parentNode) {
          this.variantOptionsPlaceholder.parentNode.insertBefore(
            this.variantOptionsContainer,
            this.variantOptionsPlaceholder.nextSibling
          );
          this.variantOptionsPlaceholder.remove();
          this.variantOptionsPlaceholder = null;
        }
      }

      onVariantOptionClick(event) {
        const option = event.currentTarget;
        if (!this.variantSelect || option.disabled) return;

        this.variantSelect.value = option.dataset.value;
        this.updateVariantImage(option.dataset);
        this.variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
        this.setVariantOptionsOpen(false);
        this.variantToggle?.focus();
      }

      positionVariantOptions() {
        if (this.variantOptionsContainer?.hidden !== false || !this.variantOptionsContainer) return;

        const toggle = this.variantToggle;
        if (!toggle) return;

        const toggleRect = toggle.getBoundingClientRect();
        const viewportPadding = 8;
        const spaceBelow = window.innerHeight - toggleRect.bottom - viewportPadding;
        const spaceAbove = toggleRect.top - viewportPadding;
        const opensAbove = spaceBelow < 180 && spaceAbove > spaceBelow;
        const availableHeight = Math.max(48, opensAbove ? spaceAbove : spaceBelow);
        const maxHeight = Math.min(240, availableHeight);
        const top = opensAbove
          ? Math.max(viewportPadding, toggleRect.top - maxHeight - 4)
          : toggleRect.bottom + 4;
        const availableWidth = Math.max(0, window.innerWidth - viewportPadding * 2);
        const desiredWidth = Math.max(toggleRect.width, 440);
        const width = Math.min(desiredWidth, availableWidth);
        const left = Math.min(
          Math.max(viewportPadding, toggleRect.left),
          Math.max(viewportPadding, window.innerWidth - viewportPadding - width)
        );

        this.variantOptionsContainer.style.top = `${top}px`;
        this.variantOptionsContainer.style.left = `${left}px`;
        this.variantOptionsContainer.style.width = `${width}px`;
        this.variantOptionsContainer.style.maxHeight = `${maxHeight}px`;
      }

      onVariantChange() {
        if (!this.variantSelect) return;

        const selectedOption = this.variantSelect.selectedOptions[0];
        if (!selectedOption) return;

        const variantId = selectedOption.value;
        const variantTitle = selectedOption.dataset.variantTitle || selectedOption.textContent.trim();
        const productUrl = this.dataset.productUrl;
        const variantUrl = `${productUrl}?variant=${encodeURIComponent(variantId)}`;

        this.variantLinks.forEach((link) => {
          link.href = variantUrl;
        });

        if (this.variantSelected) this.variantSelected.textContent = variantTitle;
        this.variantOptions?.forEach((option) => {
          const isSelected = option.dataset.value === variantId;
          option.setAttribute('aria-selected', String(isSelected));
          option.tabIndex = isSelected ? 0 : -1;
        });

        this.updateVariantImage(selectedOption.dataset);

        if (this.priceElement && selectedOption.dataset.price) {
          this.priceElement.textContent = selectedOption.dataset.price;
        }

        if (this.compareAtPriceElement) {
          const compareAtPrice = selectedOption.dataset.compareAtPrice || '';
          this.compareAtPriceElement.textContent = compareAtPrice;
          this.compareAtPriceElement.hidden = compareAtPrice === '';
        }

        const isAvailable = !selectedOption.disabled;
        this.submitButton.disabled = !isAvailable;
        this.submitButton.setAttribute('aria-disabled', String(!isAvailable));
        this.submitButton.setAttribute(
          'aria-label',
          `${this.dataset.addLabel || 'Add to cart'}: ${this.dataset.productTitle || ''} — ${variantTitle}`
        );
      }

      updateVariantImage(variantData) {
        if (!this.image || !variantData?.image) return;

        const imageSrcset = variantData.imageSrcset || variantData.image;
        this.image.setAttribute('srcset', imageSrcset);
        this.image.setAttribute('src', variantData.image);
        this.image.setAttribute('alt', variantData.imageAlt || this.dataset.productTitle || '');
      }

      async onSubmit(event) {
        event.preventDefault();

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.clearError();
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.submitButton.querySelector('.loading__spinner')?.classList.remove('hidden');

        try {
          const formData = new FormData(this.form);
          const sections = this.getSectionsToRender();
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          config.body = formData;

          if (sections.length > 0) {
            formData.append('sections', sections.map((section) => section.id).join(','));
            formData.append('sections_url', window.location.pathname);
          }

          const response = await fetch(`${routes.cart_add_url}`, config);
          const state = await response.json();

          if (!response.ok || state.status) {
            throw new Error(state.description || state.message || window.cartStrings.error);
          }

          const cartData = await this.getCartData();
          if (cartData) state.item_count = cartData.item_count;

          this.renderCartState(state);
        } catch (error) {
          console.error('Unable to add the popular add-on to the cart:', error);
          this.showError(error.message || window.cartStrings.error);
        } finally {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.querySelector('.loading__spinner')?.classList.add('hidden');
          this.onVariantChange();
        }
      }

      getSectionsToRender() {
        const sections = [];
        const cartDrawer = document.querySelector('cart-drawer');
        const mainCartItems = document.getElementById('main-cart-items');
        const mainCartFooter = document.getElementById('main-cart-footer');
        const mainCartPopularAddOns = document.getElementById('main-cart-popular-add-ons');

        if (cartDrawer) {
          sections.push({ id: 'cart-drawer' });
        }

        if (document.getElementById('cart-icon-bubble')) sections.push({ id: 'cart-icon-bubble' });

        if (mainCartItems?.dataset.id) sections.push({ id: mainCartItems.dataset.id });
        if (mainCartFooter?.dataset.id) sections.push({ id: mainCartFooter.dataset.id });
        if (mainCartPopularAddOns?.dataset.id) sections.push({ id: mainCartPopularAddOns.dataset.id });

        return sections;
      }

      renderCartState(state) {
        const cartDrawer = document.querySelector('cart-drawer');

        if (cartDrawer && state.sections?.['cart-drawer']) {
          cartDrawer.renderContents(state);
        } else {
          this.replaceCartIcon(state);
        }

        this.replaceMainCartSection(state, 'main-cart-items', '.js-contents');
        this.replaceMainCartSection(state, 'main-cart-footer', '.js-contents');
        this.replaceMainCartSection(state, 'main-cart-popular-add-ons', '.js-contents');

        if (typeof state.item_count === 'number') {
          document.querySelector('cart-items')?.classList.toggle('is-empty', state.item_count === 0);
          document.getElementById('main-cart-footer')?.classList.toggle('is-empty', state.item_count === 0);
          document.getElementById('main-cart-popular-add-ons')?.classList.toggle('is-empty', state.item_count === 0);
          this.updateCustomCartCount(state.item_count);
        }
      }

      async getCartData() {
        try {
          const shopRoot = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${shopRoot}cart.js`, {
            headers: { Accept: 'application/json' },
          });

          if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);

          return await response.json();
        } catch (error) {
          console.error('Unable to refresh the cart count after adding a popular add-on:', error);
          return null;
        }
      }

      replaceMainCartSection(state, elementId, selector) {
        const targetElement = document.getElementById(elementId);
        const sectionId = targetElement?.dataset.id;

        if (!targetElement || !sectionId || !state.sections?.[sectionId]) return;

        const sourceDocument = new DOMParser().parseFromString(state.sections[sectionId], 'text/html');
        const sourceElement = sourceDocument.querySelector(selector);
        const destinationElement = targetElement.querySelector(selector);

        if (sourceElement && destinationElement) destinationElement.innerHTML = sourceElement.innerHTML;
      }

      replaceCartIcon(state) {
        const destinationElement = document.getElementById('cart-icon-bubble');
        const sourceHtml = state.sections?.['cart-icon-bubble'];

        if (!destinationElement || !sourceHtml) return;

        const sourceDocument = new DOMParser().parseFromString(sourceHtml, 'text/html');
        const sourceElement = sourceDocument.querySelector('#cart-icon-bubble');

        if (sourceElement) destinationElement.innerHTML = sourceElement.innerHTML;
      }

      updateCustomCartCount(itemCount) {
        document.querySelectorAll('.gs-header a[aria-label="Cart"]').forEach((cartLink) => {
          let cartCount = cartLink.querySelector('.gs-cart-count');

          if (itemCount < 1) {
            cartCount?.remove();
            return;
          }

          if (!cartCount) {
            cartCount = document.createElement('span');
            cartCount.className = 'gs-cart-count';
            cartCount.setAttribute('aria-hidden', 'true');
            cartLink.appendChild(cartCount);
          }

          cartCount.textContent = String(itemCount);
        });
      }

      clearError() {
        if (!this.errorElement) return;

        this.errorElement.hidden = true;
        this.errorElement.textContent = '';
      }

      showError(message) {
        if (!this.errorElement) return;

        this.errorElement.hidden = false;
        this.errorElement.textContent = message;
      }
    }
  );
}
